import { NewLayoutProps } from "@components/new-ui/Layout";
import React, { ReactNode } from "react";

export type FeatureName = string;
export interface FAQEntity {
    question: string;
    answer: string | ReactNode;
}
export interface HowToStep {
    title: string;
    description: string;
}
export interface FAQSchema {
    "@type": "FAQPage";
    mainEntity: {
        "@type": "Question";
        name: string;
        acceptedAnswer: {
            "@type": "Answer";
            text: string;
        };
    }[];
}

export function extractTextFromJSX(node: ReactNode): string {
    if (typeof node === "string" || typeof node === "number") {
        return String(node);
    }

    if (Array.isArray(node)) {
        return node.map(extractTextFromJSX).join(" ");
    }

    if (React.isValidElement(node) && node.props?.children !== undefined) {
        return extractTextFromJSX(node.props.children);
    }

    return "";
}

export function convertFAQToSchemaFromReactNode(faqItems: FAQEntity[]): FAQSchema {
    const mainEntity = faqItems.map((item) => ({
        "@type": "Question" as const,
        name: item.question,
        acceptedAnswer: {
            "@type": "Answer" as const,
            text: typeof item.answer === "string" ? item.answer : extractTextFromJSX(item.answer),
        },
    }));

    return {
        "@type": "FAQPage",
        mainEntity,
    };
}

export function schemaGenerator(props: NewLayoutProps) {
    if (!props.schema) return null;
    const { schema, title, description, canonical } = props;
    const { heroOrOgImageUrl, heroOrOgImageAlt, heroOrOgImageDescription, alternativesInfo, faq = [], softwareFeatureName, howTo } = schema;

    const today = new Date().toISOString().split("T")[0];
    const faqSchema = convertFAQToSchemaFromReactNode(faq);

    const baseSchema = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": canonical,
        url: canonical,
        name: title,
        thumbnailUrl: heroOrOgImageAlt,
        datePublished: today,
        dateModified: today,
        description: description,
        inLanguage: "en-US",

        isPartOf: { "@type": "WebSite", "@id": canonical, url: canonical, name: "gallabox", description: description, inLanguage: "en-IN" },

        primaryImageOfPage: {
            "@type": "ImageObject",
            "@id": heroOrOgImageUrl,
            inLanguage: "en-IN",
            url: heroOrOgImageUrl,
            contentUrl: heroOrOgImageDescription,
            width: "600",
            height: "600",
            caption: heroOrOgImageAlt,
        },
        image: {
            "@type": "ImageObject",
            "@id": heroOrOgImageUrl,
            inLanguage: "en-IN",
            url: heroOrOgImageUrl,
            contentUrl: heroOrOgImageDescription,
            width: "600",
            height: "600",
            caption: heroOrOgImageAlt,
        },
    };
    const graph: any[] = [baseSchema];

    if (faqSchema && faq.length > 0) {
        graph.push(faqSchema);
    }

    if (softwareFeatureName) {
        const softwareApplicationSchema = {
            "@type": "SoftwareApplication",
            name: softwareFeatureName,
            applicationCategory: "BusinessApplication",
            image: heroOrOgImageUrl,
            description: description,
            inLanguage: "en-US",
            datePublished: today,
            dateModified: today,
            aggregateRating: {
                "@type": "AggregateRating",
                ratingValue: "4.9",
                ratingCount: "460",
                bestRating: "5",
                worstRating: "0",
            },
            offers: {
                "@type": "Offer",
                price: "999",
                priceCurrency: "INR",
            },
        };
        graph.push(softwareApplicationSchema);
    }

    if (howTo && howTo?.howtoSteps.length > 0) {
        const howToschemaApplicationSchema = {
            "@type": "HowTo",
            "@id": canonical,
            name: title,
            description: description,
            url: canonical,
            totalTime: "PT1M",
            inLanguage: "en-US",
            image: {
                "@type": "ImageObject",
                "@id": heroOrOgImageUrl,
                url: heroOrOgImageUrl,
                width: "600px",
                height: "400px",
                caption: heroOrOgImageAlt,
                inLanguage: "en-US",
            },
            step: howTo?.howtoSteps.map((step, index) => ({
                "@type": "HowToStep",
                position: String(index + 1),
                text: step.title,
            })),
        };
        graph.push(howToschemaApplicationSchema);
    }

    if (alternativesInfo) {
        const listItemSchema = {
            "@type": "ItemList",
            name: title,
            numberOfItems: alternativesInfo?.length,
            itemListOrder: "https://schema.org/ItemListOrderAscending",
            itemListElement: alternativesInfo?.map((item, index) => ({
                "@type": "ListItem",
                position: String(index + 1),
                item: {
                    "@type": "SoftwareApplication",
                    name: item.title,
                    url: canonical,
                    image: item.imageUrl,
                    description: item.description,
                    operatingSystem: "Web",
                },
            })),
        };
        graph.push(listItemSchema);
    }

    return {
        "@context": "https://schema.org",
        "@graph": graph,
    };
}
